import type { PendingSyncStore } from "./PendingSyncStore";

// The one place the destination of a finished record is decided. A signed-in
// player's online match is the server's record — it is queued in the outbox
// (so it is never lost even if the tab closes before the network call
// resolves) and a sync is attempted immediately; it is never written to
// `localStorage`. Everything else (offline modes, or an anonymous player's
// online match) is a device record only.
export interface CommitRecordOptions<TRecord> {
  readonly record: TRecord;
  // Stamps the outbox entry and scopes the immediate flush attempt — see
  // PendingSyncStore's account-matching rule.
  readonly accountId: string;
  readonly isOnlineMatch: boolean;
  readonly signedIn: boolean;
  readonly saveLocal: (record: TRecord) => void;
  readonly syncRemote: (record: TRecord) => Promise<boolean>;
  readonly outbox: PendingSyncStore<TRecord>;
}

export function commitFinishedRecord<TRecord>(options: CommitRecordOptions<TRecord>): void {
  const { record, accountId, isOnlineMatch, signedIn, saveLocal, syncRemote, outbox } = options;

  if (isOnlineMatch && signedIn) {
    // Enqueue synchronously first, then attempt to flush (this entry plus any
    // still-pending ones for the same account) — an ack dequeues, a failure
    // leaves it queued for the next app start or sign-in.
    outbox.enqueue(accountId, record);
    void outbox.flush(accountId, syncRemote);
    return;
  }

  saveLocal(record);
}
